import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, TextField, Button, Typography,
  InputAdornment, IconButton, Alert, CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { API_ENDPOINTS } from '../config';
import { BRAND } from '../theme';

export default function ResetPassword() {
  const { uid: routeUid, token: routeToken } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = routeToken || searchParams.get('token') || '';
  const uid   = routeUid   || searchParams.get('uid')   || '';

  const [password, setPassword]       = useState('');
  const [confirm, setConfirm]         = useState('');
  const [showPw, setShowPw]           = useState(false);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');
  // Track the post-success redirect timer so it can be cleared if the user
  // navigates away before it fires (otherwise React warns about state updates
  // on an unmounted component, and the user gets bounced unexpectedly).
  const redirectTimerRef = useRef(null);
  useEffect(() => () => {
    if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.PASSWORD_RESET_CONFIRM, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, uid, password }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(data.message || 'Password reset successfully.');
        redirectTimerRef.current = setTimeout(() => navigate('/login'), 2500);
      } else {
        setError(data.message || 'Reset failed. The link may have expired.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Both `token` AND `uid` must be present — the backend looks up the user
  // from `uid` and validates the stateless reset token against that user.
  // A link missing either piece can't be validated, so reject up-front.
  if (!token || !uid) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
        <Alert severity="error">Invalid reset link. Please request a new one.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
      <Card sx={{ width: 420, p: 2, boxShadow: 4, borderRadius: 3, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 3,
          background: `linear-gradient(90deg, ${BRAND.gopaGold} 0%, ${BRAND.gopaGold} 52%, ${BRAND.gizRed} 52%, ${BRAND.gizRed} 100%)`,
        }} />
        <CardContent sx={{ pt: 2.5 }}>
          <Typography variant="h5" fontWeight={700} color={BRAND.primaryMain} gutterBottom>
            Set New Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose a strong password for your PRUDEV II account.
          </Typography>

          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {!success && (
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                label="New password"
                type={showPw ? 'text' : 'password'}
                fullWidth
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPw(p => !p)} edge="end">
                        {showPw ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Confirm new password"
                type="password"
                fullWidth
                required
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                sx={{ mb: 3 }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{ py: 1.5, bgcolor: BRAND.primaryMain, '&:hover': { bgcolor: BRAND.primaryDark }, fontWeight: 700, borderRadius: 2 }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : 'Reset Password'}
              </Button>
              <Button
                fullWidth
                sx={{ mt: 1, color: BRAND.primaryMain }}
                onClick={() => navigate('/login')}
              >
                Back to Sign In
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
